import { useState, useEffect } from 'react';
import { Folder } from '../types';
import { supabase } from '../lib/supabase';
import { supabaseQuery, LoadingStateManager, createCleanupManager } from '../utils/dataLoader';

export const useFolders = (userId: string | null) => {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingManager] = useState(() => new LoadingStateManager());

  useEffect(() => {
    const cleanup = createCleanupManager();
    
    if (userId) {
      // Only load folders if tab is visible
      if (!document.hidden) {
        // Load folders with delay to not block initial render
        const timeout = setTimeout(() => {
          if (!document.hidden) loadFolders();
        }, 150);
        cleanup.addTimeout(timeout);
      }
    } else {
      setFolders([]);
      loadingManager.reset();
      setIsLoading(false);
    }
    
    return cleanup.cleanup;
  }, [userId]);

  const loadFolders = async () => {
    if (!userId) return;
    
    // Skip loading if tab is not visible
    if (document.hidden) {
      console.log('🔵 Folders: Skipping load - tab not visible');
      return;
    }

    try {
      setIsLoading(loadingManager.shouldShowLoading(folders.length));
      setError(null);

      const data = await supabaseQuery(
        () => supabase
          .from('folders')
          .select('*')
          .eq('user_id', userId)
          .order('name', { ascending: true }),
        'Load Folders',
        { timeout: 5000, maxRetries: 2 }
      );

      const processedFolders = (data || []).map(item => ({
        id: item.id,
        name: item.name,
        parentId: item.parent_id,
        color: item.color || '#6B7280',
        userId: item.user_id,
        createdAt: new Date(item.created_at),
        updatedAt: new Date(item.updated_at),
      }));

      setFolders(processedFolders);
      loadingManager.markDataLoaded(processedFolders.length);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load folders';
      setError(errorMessage);
      loadingManager.markDataLoaded(0);
    } finally {
      setIsLoading(false);
    }
  };

  const createFolder = async (folderData: {
    name: string;
    parentId?: string;
    color?: string;
  }): Promise<Folder | null> => {
    console.log('🔵 useFolders: createFolder called', {
      userId,
      folderData,
      hasUserId: !!userId
    });

    if (!userId) {
      console.error('❌ useFolders: No userId available');
      throw new Error('You must be logged in to create folders');
    }

    try {
      const data = await supabaseQuery(
        () => supabase
          .from('folders')
          .insert({
            user_id: userId,
            name: folderData.name,
            parent_id: folderData.parentId || null,
            color: folderData.color || '#6B7280',
          })
          .select()
          .single(),
        'Create Folder',
        { timeout: 5000, maxRetries: 1 }
      );
      
      if (!data) {
        throw new Error('Failed to create folder - no data returned');
      }

      const newFolder: Folder = {
        id: data.id,
        name: data.name,
        parentId: data.parent_id,
        color: data.color,
        userId: data.user_id,
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at),
      };

      setFolders(prevFolders => [...prevFolders, newFolder]);
      return newFolder;
    } catch (err) {
      throw err;
    }
  };

  const updateFolder = async (
    id: string,
    updates: Partial<Folder>
  ): Promise<void> => {
    try {
      const updateData: any = {};
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.parentId !== undefined) updateData.parent_id = updates.parentId;
      if (updates.color !== undefined) updateData.color = updates.color;

      const { error } = await supabase
        .from('folders')
        .update(updateData)
        .eq('id', id);

      if (error) {
        console.error('Error updating folder:', error);
        throw new Error(error.message);
      }

      setFolders(prev =>
        prev.map(folder =>
          folder.id === id ? { ...folder, ...updates } : folder
        )
      );
    } catch (err) {
      console.error('Error updating folder:', err);
      throw err;
    }
  };

  const deleteFolder = async (id: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('folders')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting folder:', error);
        throw new Error(error.message);
      }

      setFolders(prev => prev.filter(folder => folder.id !== id));
    } catch (err) {
      console.error('Error deleting folder:', err);
      throw err;
    }
  };

  const getFolderPath = (folderId: string): Folder[] => {
    const path: Folder[] = [];
    let currentFolder = folders.find(f => f.id === folderId);
    
    while (currentFolder) {
      path.unshift(currentFolder);
      currentFolder = currentFolder.parentId 
        ? folders.find(f => f.id === currentFolder!.parentId)
        : undefined;
    }
    
    return path;
  };

  const getSubfolders = (parentId?: string): Folder[] => {
    return folders.filter(folderItem => folderItem.parentId === parentId);
  };

  const getRootFolders = (): Folder[] => {
    return folders.filter(folderItem => !folderItem.parentId);
  };

  return {
    folders,
    isLoading,
    error,
    createFolder,
    updateFolder,
    deleteFolder,
    getFolderPath,
    getSubfolders,
    getRootFolders,
    refreshFolders: loadFolders,
  };
};